import React, { useRef, useState, useMemo } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Star, MapPin, Phone, Clock, Instagram, Facebook, ArrowUpRight, Navigation } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/site/Reveal";
import { useBooking } from "@/context/BookingContext";

const inr = (n) => "₹" + Number(n).toLocaleString("en-IN");
const HERO_IMG =
  "https://images.pexels.com/photos/30825617/pexels-photo-30825617.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=940";
const INTERIOR_IMG =
  "https://images.unsplash.com/photo-1595871151608-bc7abd1caca3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBzcGElMjBzYWxvbiUyMGludGVyaW9yfGVufDB8fHx8MTc4NzI1MjU4MHww&ixlib=rb-4.1.0&q=85";

/* ---------------- HERO ---------------- */
export function Hero() {
  const { openBooking, settings, services } = useBooking();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  const featured =
    services.find((s) => /bridal makeup/i.test(s.name)) ||
    services.find((s) => s.featured) ||
    services[0];
  const featuredPrice = featured ? (featured.discount_price ?? featured.price) : null;
  const serviceCount = services.length;

  return (
    <section ref={ref} id="home" className="relative min-h-screen pt-32 pb-16 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-[11px] tracking-[0.32em] uppercase text-[#8E3B46] font-semibold mb-6 flex items-center gap-3"
          >
            <span className="h-px w-10 bg-[#D4AF37]" />
            Bankra · Howrah · Est. Ladies Parlour
          </motion.p>

          <h1 className="font-display font-light text-[#2A1B1D] text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
            <MaskedLines lines={["Your Beauty,", "Our Passion."]} delay={0.35} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-7 max-w-md text-[#5C4A4D] leading-relaxed"
          >
            A boutique ladies beauty parlour crafting radiant skin, elegant hair and unforgettable
            bridal looks — with warmth, care and modern artistry.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.8 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => openBooking()}
              data-testid="hero-book-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-[#8E3B46] px-8 py-4 text-sm font-medium text-[#FDFBF7] transition-colors duration-300 hover:bg-[#722F38]"
            >
              Book Appointment
              <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <a
              href="#services"
              className="inline-flex items-center rounded-full border border-[#2A1B1D]/15 px-8 py-4 text-sm font-medium text-[#2A1B1D] transition-colors duration-300 hover:border-[#8E3B46] hover:text-[#8E3B46]"
            >
              Explore Services
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5, duration: 0.9 }}
            className="mt-12 flex items-center gap-8"
          >
            <Stat value={serviceCount > 0 ? `${serviceCount}+` : "—"} label="Signature Services" />
            <span className="h-8 w-px bg-[#2A1B1D]/10" />
            <Stat value="500+" label="Happy Clients" />
            <span className="h-8 w-px bg-[#2A1B1D]/10" />
            <Stat value="4.9★" label="Avg. Rating" />
          </motion.div>
        </div>

        <div className="lg:col-span-6 relative">
          <motion.div
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 1.2, ease: [0.76, 0, 0.24, 1] }}
            className="relative arch overflow-hidden shadow-[0_30px_80px_rgba(42,27,29,0.18)]"
            style={{ aspectRatio: "3/4" }}
          >
            <motion.img
              src={HERO_IMG}
              alt="Elegant bridal beauty"
              style={{ y, scale }}
              className="absolute inset-0 h-[122%] w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2A1B1D]/25 via-transparent to-transparent" />
          </motion.div>

          {featured && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3, duration: 0.8 }}
              className="absolute -bottom-5 -left-4 sm:left-2 bg-[#FDFBF7] rounded-2xl px-5 py-4 shadow-[0_16px_40px_rgba(42,27,29,0.12)] border border-[#D4AF37]/25"
              data-testid="hero-featured-badge"
            >
              <p className="text-[10px] tracking-[0.22em] uppercase text-[#8E3B46] font-semibold">Signature</p>
              <p className="font-display text-2xl text-[#2A1B1D] leading-tight">{featured.name}</p>
              <p className="text-xs text-[#5C4A4D]">from {inr(featuredPrice)}</p>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <p className="font-display text-3xl text-[#2A1B1D] leading-none">{value}</p>
      <p className="text-[11px] text-[#5C4A4D] mt-1">{label}</p>
    </div>
  );
}

/* ---------------- MARQUEE ---------------- */
export function BeautyMarquee() {
  const items = ["Facial", "Bridal Makeup", "Hair Spa", "Mehendi", "Waxing", "Manicure", "Keratin", "Threading"];
  return (
    <section className="py-8 border-y border-[#D4AF37]/20 bg-[#F4EFEA]/60">
      <Marquee speed={26} gradient={false}>
        {items.map((t, i) => (
          <span key={i} className="mx-8 font-display text-4xl sm:text-5xl font-light text-[#2A1B1D] flex items-center gap-8">
            {t}
            <span className="text-[#D4AF37]">✦</span>
          </span>
        ))}
      </Marquee>
    </section>
  );
}

/* ---------------- MANIFESTO ---------------- */
export function Manifesto() {
  const chapters = [
    { n: "01", title: "Crafted for Women", text: "A calm, private space designed entirely around the comfort and confidence of women." },
    { n: "02", title: "Premium Products", text: "We use trusted, skin-loving brands so every treatment feels as good as it looks." },
    { n: "03", title: "Honest Pricing", text: "Transparent, always-current prices. What you see online is exactly what you pay." },
  ];
  return (
    <section id="about" className="py-24 sm:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="text-[11px] tracking-[0.28em] uppercase text-[#8E3B46] font-semibold">Why Munmun</p>
            <h2 className="font-display font-light text-4xl sm:text-5xl text-[#2A1B1D] mt-4 leading-tight">
              Beauty, delivered with intention and warmth.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 arch overflow-hidden shadow-[0_20px_60px_rgba(42,27,29,0.12)]" style={{ aspectRatio: "4/5" }}>
              <img src={INTERIOR_IMG} alt="Salon interior" className="h-full w-full object-cover" />
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7 lg:pl-10 space-y-12">
          {chapters.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.1}>
              <div className="relative border-b border-[#2A1B1D]/10 pb-10">
                <span className="font-display text-8xl sm:text-9xl text-[#D4AF37]/25 leading-none absolute -top-6 -left-2 select-none">
                  {c.n}
                </span>
                <div className="relative pl-4">
                  <h3 className="font-display text-3xl text-[#2A1B1D]">{c.title}</h3>
                  <p className="text-[#5C4A4D] mt-3 max-w-lg leading-relaxed">{c.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- SERVICES ---------------- */
export function Services() {
  const { services, openBooking } = useBooking();
  const categories = useMemo(() => ["All", ...Array.from(new Set(services.map((s) => s.category)))], [services]);
  const [active, setActive] = useState("All");
  const filtered = active === "All" ? services : services.filter((s) => s.category === active);

  return (
    <section id="services" className="py-24 sm:py-32 bg-[#F4EFEA]/50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <Reveal>
            <p className="text-[11px] tracking-[0.28em] uppercase text-[#8E3B46] font-semibold">The Menu</p>
            <h2 className="font-display font-light text-4xl sm:text-5xl text-[#2A1B1D] mt-3">Services & Prices</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-2" data-testid="service-filters">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setActive(c)}
                  className={`rounded-full px-4 py-2 text-xs font-medium transition-colors duration-300 ${
                    active === c
                      ? "bg-[#8E3B46] text-[#FDFBF7]"
                      : "border border-[#2A1B1D]/12 text-[#5C4A4D] hover:border-[#8E3B46] hover:text-[#8E3B46]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="services-grid">
          {filtered.map((s, i) => (
            <Reveal key={s.id} delay={(i % 3) * 0.08}>
              <ServiceCard s={s} onBook={() => openBooking(s.id)} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ s, onBook }) {
  const hasDiscount = s.discount_price != null && s.discount_price < s.price;
  const pct = hasDiscount ? Math.round((1 - s.discount_price / s.price) * 100) : 0;
  return (
    <div
      className="group bg-[#FDFBF7] rounded-3xl border border-[#2A1B1D]/8 overflow-hidden transition-all duration-500 hover:shadow-[0_24px_60px_rgba(42,27,29,0.12)] hover:-translate-y-1"
      data-testid={`service-card-${s.id}`}
    >
      <div className="relative h-52 overflow-hidden">
        {s.image ? (
          <img src={s.image} alt={s.name} className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
        ) : (
          <div className="h-full w-full bg-[#F4EFEA] flex items-center justify-center">
            <span className="font-display text-3xl text-[#D4AF37]/60">{s.name.charAt(0)}</span>
          </div>
        )}
        <span className="absolute top-3 left-3 rounded-full bg-[#FDFBF7]/90 backdrop-blur px-3 py-1 text-[10px] font-semibold tracking-wide uppercase text-[#8E3B46]">
          {s.category}
        </span>
        {hasDiscount && (
          <span className="absolute top-3 right-3 rounded-full bg-[#D4AF37] px-3 py-1 text-[10px] font-bold text-[#2A1B1D]">
            {pct}% OFF
          </span>
        )}
      </div>
      <div className="p-6">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-2xl text-[#2A1B1D] leading-tight">{s.name}</h3>
          <span className="shrink-0 text-xs text-[#5C4A4D] flex items-center gap-1 mt-1">
            <Clock size={13} /> {s.duration}m
          </span>
        </div>
        <p className="text-sm text-[#5C4A4D] mt-2 line-clamp-2 min-h-[40px]">{s.description}</p>
        <div className="mt-5 flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-2xl text-[#8E3B46]">{inr(s.discount_price ?? s.price)}</span>
            {hasDiscount && <span className="text-sm text-[#5C4A4D] line-through">{inr(s.price)}</span>}
          </div>
          <button
            onClick={onBook}
            data-testid={`book-service-${s.id}`}
            className="rounded-full border border-[#8E3B46] px-4 py-2 text-xs font-medium text-[#8E3B46] transition-colors duration-300 hover:bg-[#8E3B46] hover:text-[#FDFBF7]"
          >
            Book Now
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- OFFERS ---------------- */
export function Offers({ offers }) {
  const { openBooking } = useBooking();
  return (
    <section id="offers" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <p className="text-[11px] tracking-[0.28em] uppercase text-[#8E3B46] font-semibold">Limited Time</p>
          <h2 className="font-display font-light text-4xl sm:text-5xl text-[#2A1B1D] mt-3 mb-12">Special Offers</h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {offers.map((o, i) => (
            <Reveal key={o.id} delay={i * 0.1} className={i === 0 ? "md:col-span-6" : "md:col-span-3"}>
              <div className="group relative h-72 rounded-3xl overflow-hidden" data-testid={`offer-${o.id}`}>
                <img src={o.image} alt={o.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2A1B1D]/85 via-[#2A1B1D]/25 to-transparent" />
                <div className="absolute inset-0 p-6 flex flex-col justify-end">
                  <span className="inline-flex w-fit rounded-full bg-[#D4AF37] px-3 py-1 text-[11px] font-bold text-[#2A1B1D] mb-3">{o.discount}</span>
                  <h3 className="font-display text-2xl text-[#FDFBF7] leading-tight">{o.title}</h3>
                  <p className="text-sm text-[#FDFBF7]/80 mt-1 line-clamp-2">{o.description}</p>
                  <button onClick={() => openBooking()} className="mt-4 w-fit text-xs font-medium text-[#FDFBF7] border-b border-[#D4AF37] pb-0.5 hover:text-[#D4AF37] transition-colors">
                    Claim Offer →
                  </button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- PACKAGES ---------------- */
export function Packages({ packages }) {
  const { openBooking } = useBooking();
  return (
    <section id="packages" className="py-24 sm:py-32 bg-[#2A1B1D] text-[#FDFBF7] relative overflow-hidden grain">
      <div className="max-w-7xl mx-auto px-6 relative">
        <Reveal>
          <p className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] font-semibold">Curated Bundles</p>
          <h2 className="font-display font-light text-4xl sm:text-5xl mt-3 mb-12">Beauty Packages</h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {packages.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1}>
              <div className="group rounded-3xl border border-[#FDFBF7]/12 bg-[#FDFBF7]/[0.03] overflow-hidden h-full flex flex-col" data-testid={`package-${p.id}`}>
                <div className="h-44 overflow-hidden">
                  <img src={p.image} alt={p.name} className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-display text-2xl">{p.name}</h3>
                  <ul className="mt-4 space-y-2 flex-1">
                    {p.includes.map((inc) => (
                      <li key={inc} className="text-sm text-[#FDFBF7]/70 flex items-center gap-2">
                        <span className="text-[#D4AF37]">✦</span> {inc}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 flex items-center justify-between">
                    <span className="font-display text-3xl text-[#D4AF37]">{inr(p.price)}</span>
                    <button onClick={() => openBooking()} className="rounded-full bg-[#FDFBF7] px-5 py-2 text-xs font-medium text-[#2A1B1D] hover:bg-[#D4AF37] transition-colors">
                      Enquire
                    </button>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- GALLERY ---------------- */
export function Gallery({ gallery }) {
  return (
    <section id="gallery" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <p className="text-[11px] tracking-[0.28em] uppercase text-[#8E3B46] font-semibold">Our Work</p>
          <h2 className="font-display font-light text-4xl sm:text-5xl text-[#2A1B1D] mt-3 mb-12">The Gallery</h2>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {gallery.map((g, i) => (
            <Reveal key={g.id} delay={(i % 4) * 0.06} className={i % 5 === 0 ? "row-span-2" : ""}>
              <div className={`group overflow-hidden rounded-2xl ${i % 5 === 0 ? "h-full min-h-[320px]" : "h-56"}`} data-testid={`gallery-${g.id}`}>
                <img src={g.url} alt="Gallery" className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-110" />
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- REVIEWS ---------------- */
export function Reviews({ reviews }) {
  return (
    <section id="reviews" className="py-24 sm:py-32 bg-[#F4EFEA]/50">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <p className="text-[11px] tracking-[0.28em] uppercase text-[#8E3B46] font-semibold">Loved By</p>
          <h2 className="font-display font-light text-4xl sm:text-5xl text-[#2A1B1D] mt-3 mb-12">Client Stories</h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {reviews.map((r, i) => (
            <Reveal key={r.id} delay={(i % 4) * 0.08}>
              <div className="bg-[#FDFBF7] rounded-3xl p-7 border border-[#2A1B1D]/8 h-full flex flex-col" data-testid={`review-${r.id}`}>
                <div className="flex gap-0.5 text-[#D4AF37]">
                  {Array.from({ length: r.rating }).map((_, k) => (
                    <Star key={k} size={16} fill="currentColor" />
                  ))}
                </div>
                <p className="font-display text-xl text-[#2A1B1D] mt-4 leading-snug flex-1">"{r.text}"</p>
                <div className="mt-5">
                  <p className="text-sm font-medium text-[#2A1B1D]">{r.name}</p>
                  <p className="text-xs text-[#8E3B46]">{r.service}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- CONTACT ---------------- */
export function Contact() {
  const { settings, openBooking } = useBooking();
  const wa = settings?.whatsapp || "918240037030";
  const maps = settings?.maps_url || "https://www.google.com/maps";
  return (
    <section id="contact" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-14">
        <div>
          <Reveal>
            <p className="text-[11px] tracking-[0.28em] uppercase text-[#8E3B46] font-semibold">Visit Us</p>
            <h2 className="font-display font-light text-4xl sm:text-5xl text-[#2A1B1D] mt-3">Come say hello.</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 space-y-6">
              <InfoRow icon={<MapPin size={18} />} title="Location" text={settings?.address} />
              <InfoRow icon={<Phone size={18} />} title="Phone" text={settings?.phone} />
              <InfoRow icon={<Clock size={18} />} title="Opening Hours" text="Mon–Fri 5PM–11PM · Sat–Sun 11AM–11PM" />
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-9 flex flex-wrap gap-3">
              <a href={maps} target="_blank" rel="noopener noreferrer" data-testid="get-directions-btn" className="inline-flex items-center gap-2 rounded-full bg-[#8E3B46] px-6 py-3 text-sm font-medium text-[#FDFBF7] hover:bg-[#722F38] transition-colors">
                <Navigation size={16} /> Get Directions
              </a>
              <a href={`tel:${(settings?.phone || "").replace(/\s/g, "")}`} data-testid="call-now-btn" className="inline-flex items-center gap-2 rounded-full border border-[#2A1B1D]/15 px-6 py-3 text-sm font-medium text-[#2A1B1D] hover:border-[#8E3B46] hover:text-[#8E3B46] transition-colors">
                <Phone size={16} /> Call Now
              </a>
              <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-[#25D366] px-6 py-3 text-sm font-medium text-[#1a9e4b] hover:bg-[#25D366] hover:text-white transition-colors">
                WhatsApp
              </a>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <div className="rounded-3xl overflow-hidden h-full min-h-[360px] border border-[#D4AF37]/25 shadow-[0_20px_60px_rgba(42,27,29,0.1)]">
            <iframe
              title="Munmun Ladies Beauty Parlour location"
              src="https://www.google.com/maps?q=Bankra+Howrah+West+Bengal+711403&output=embed"
              className="h-full w-full min-h-[360px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function InfoRow({ icon, title, text }) {
  return (
    <div className="flex items-start gap-4">
      <span className="mt-1 grid place-items-center h-10 w-10 rounded-full bg-[#F4EFEA] text-[#8E3B46] shrink-0">{icon}</span>
      <div>
        <p className="text-xs tracking-[0.18em] uppercase text-[#8E3B46] font-semibold">{title}</p>
        <p className="text-[#2A1B1D] mt-1 max-w-xs leading-relaxed">{text}</p>
      </div>
    </div>
  );
}

/* ---------------- FOOTER ---------------- */
export function Footer() {
  const { settings } = useBooking();
  return (
    <footer className="bg-[#2A1B1D] text-[#FDFBF7] pt-16 pb-8 grain">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 pb-12 border-b border-[#FDFBF7]/10">
          <div>
            <p className="font-display text-3xl">Munmun</p>
            <p className="text-sm text-[#FDFBF7]/60 mt-3 max-w-xs">{settings?.tagline || "Your Beauty, Our Passion"} — a ladies beauty parlour in Bankra, Howrah.</p>
            <div className="flex gap-3 mt-5">
              <a href={settings?.instagram || "#"} target="_blank" rel="noopener noreferrer" className="grid place-items-center h-10 w-10 rounded-full border border-[#FDFBF7]/15 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"><Instagram size={18} /></a>
              <a href={settings?.facebook || "#"} target="_blank" rel="noopener noreferrer" className="grid place-items-center h-10 w-10 rounded-full border border-[#FDFBF7]/15 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"><Facebook size={18} /></a>
            </div>
          </div>
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-semibold mb-4">Explore</p>
            <ul className="space-y-2 text-sm text-[#FDFBF7]/70">
              {["Services", "Offers", "Packages", "Gallery", "Reviews", "Contact"].map((l) => (
                <li key={l}><a href={`#${l.toLowerCase()}`} className="hover:text-[#D4AF37] transition-colors">{l}</a></li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-semibold mb-4">Reach Us</p>
            <p className="text-sm text-[#FDFBF7]/70 leading-relaxed">{settings?.address}</p>
            <p className="text-sm text-[#FDFBF7]/70 mt-3">{settings?.phone}</p>
          </div>
        </div>
        <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#FDFBF7]/50">
          <p>© {new Date().getFullYear()} Munmun Ladies Beauty Parlour. All rights reserved.</p>
          <div className="flex items-center gap-5">
            <a href="/privacy-policy" className="hover:text-[#D4AF37] transition-colors" data-testid="footer-privacy-link">Privacy Policy</a>
            <a href="/delete-account" className="hover:text-[#D4AF37] transition-colors" data-testid="footer-delete-account-link">Delete Account</a>
            <a href="/admin/login" className="hover:text-[#D4AF37] transition-colors" data-testid="footer-admin-link">Owner Login</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
